import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { inclusionTravelPeriodFilter } from './inclusionsTravelPeriodFilter'
import { partitionBy } from 'lib/array/arrayUtils'
import {
  EXCLUDED_SEARCH_INCLUSION_FILTER_TYPES,
  inclusionCategoryMap,
  inclusionFilterTypeIconMap,
  inclusionIconMap,
  type InclusionFilterType,
} from 'constants/inclusions'

interface GetInclusionsOptions {
  checkIn?: moment.Moment | string
  checkOut?: moment.Moment | string
  luxPlusTier?: App.MembershipTier
}

export function extractPkgInclusions(pkg: App.Package | undefined, options: GetInclusionsOptions = {}) {
  const { checkIn, checkOut, luxPlusTier } = options

  if (!pkg) {
    return [[], [], [], []]
  }

  const isoCheckIn = typeof checkIn === 'string' ? checkIn : checkIn?.format(ISO_DATE_FORMAT)
  const isoCheckOut = typeof checkOut === 'string' ? checkOut : checkOut?.format(ISO_DATE_FORMAT)
  const [bonusInclusionsRaw, inclusionsRaw] = partitionBy(pkg.inclusions ?? [], inclusion => !!inclusion?.isBonus)
  const inclusions = inclusionTravelPeriodFilter(inclusionsRaw, isoCheckIn, isoCheckOut)
  const bonusInclusions = inclusionTravelPeriodFilter([...bonusInclusionsRaw, ...pkg.bonusInclusions], isoCheckIn, isoCheckOut)
  const mainInclusions = [...inclusions, ...bonusInclusions]
  const luxPlusInclusions = luxPlusTier && pkg.luxPlusInclusionsByTier ? pkg.luxPlusInclusionsByTier[luxPlusTier as App.MembershipSubscriptionTier] ?? [] : []
  const tacticalInclusions = mainInclusions.filter(incl => incl.hasTactical)

  return [
    inclusions,
    bonusInclusions,
    luxPlusInclusions,
    tacticalInclusions,
  ]
}

export function getPkgInclusionsCount(pkg: App.Package, options: GetInclusionsOptions = {}) {
  const [inclusions, bonusInclusions, luxPlusInclusions] = extractPkgInclusions(pkg, options)

  return inclusions.length + bonusInclusions.length + luxPlusInclusions.length
}

/**
 * Resolves the svc-search inclusion filter type for an inclusion.
 * Prefers locale-stable `category`, then falls back to icon `symbol`.
 */
export function getInclusionFilterType(
  inclusion: Pick<App.OfferInclusion, 'category' | 'symbol'>,
): InclusionFilterType | undefined {
  if (inclusion.category) {
    const fromCategory = inclusionCategoryMap[inclusion.category]
    if (fromCategory) return fromCategory
  }
  return inclusion.symbol ? inclusionIconMap[inclusion.symbol] : undefined
}

/**
 * Reorders tile inclusions by boosting those that match an active inclusion filter.
 * Prefers catalogue `category`, then icon `symbol` via inclusionIconMap.
 * Non-matching inclusions maintain their original relative order.
 *
 * @param inclusions - Array of OfferInclusions to reorder
 * @param activeInclusions - Set of inclusion type strings to boost (e.g., Set(['Breakfast', 'Spa treatment']))
 * @returns New array with matching inclusions boosted to the top
 */
export function reorderTileInclusionsByActiveFilters<T extends App.OfferInclusion>(
  inclusions: Array<T>,
  activeInclusions: Set<string>,
): Array<T> {
  if (!activeInclusions.size || !inclusions.length) {
    return inclusions
  }

  const boosted: Array<T> = []
  const rest: Array<T> = []
  for (const inclusion of inclusions) {
    const inclusionType = getInclusionFilterType(inclusion)
    if (inclusionType && activeInclusions.has(inclusionType)) {
      boosted.push({ ...inclusion, isHighlighted: true })
    } else {
      rest.push({ ...inclusion, isHighlighted: false })
    }
  }

  return [...boosted, ...rest]
}

function isExcludedEsimInclusion(inclusion: Pick<App.OfferInclusion, 'description' | 'symbol' | 'id'>): boolean {
  if (inclusion.symbol === 'mobile_android') return true
  if (inclusion.id === 'loyalty_esim_benefit') return true
  const description = inclusion.description.toLowerCase()
  return description.includes('esim') || description.includes('free data roaming')
}

/** Compact tiles: omit unmapped original copy at or above this length from visible rows. */
export const UNMAPPED_INCLUSION_OMIT_MIN_CHARS = 32

function shouldOmitUnmappedInclusionFromCompactTile(
  inclusion: Pick<App.OfferInclusion, 'description'>,
): boolean {
  return inclusion.description.length >= UNMAPPED_INCLUSION_OMIT_MIN_CHARS
}

/**
 * Compact tiles: map each original inclusion to a unique Search filter label when
 * possible. Unmapped rows (including Discount/Default) keep original copy. eSIM is skipped.
 * Duplicate mapped categories are omitted so later unmapped originals can still fill the row budget.
 * Unmapped copy of 32+ characters is omitted from the visible rows and remains in View all.
 */
export function buildHybridSearchFilterInclusions(
  inclusions: Array<App.OfferInclusion>,
  maxVisibleRows: number,
  formatFilterLabel: (filterType: InclusionFilterType) => string,
): Array<App.OfferInclusion> {
  const seenFilterTypes = new Set<InclusionFilterType>()
  const rows: Array<App.OfferInclusion> = []

  for (const inclusion of inclusions) {
    if (rows.length >= maxVisibleRows) break
    if (isExcludedEsimInclusion(inclusion)) continue

    const filterType = getInclusionFilterType(inclusion)
    const canMap = !!filterType && !EXCLUDED_SEARCH_INCLUSION_FILTER_TYPES.has(filterType)

    if (!canMap) {
      if (shouldOmitUnmappedInclusionFromCompactTile(inclusion)) continue
      rows.push(inclusion)
      continue
    }

    if (seenFilterTypes.has(filterType)) continue

    seenFilterTypes.add(filterType)
    rows.push({
      id: filterType,
      description: formatFilterLabel(filterType),
      symbol: inclusionFilterTypeIconMap[filterType] ?? inclusion.symbol,
    })
  }

  return rows
}
